import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { GbpReview, GbpState, ActionToken } from '../../types/review';
import { GbpEndpoints, BASE_URL } from '../../api/endpoints';

// Get action token from URL token
export const getActionToken = createAsyncThunk(
  'gbp/getActionToken',
  async (token: string) => {
    const url = `${BASE_URL}${GbpEndpoints.getActionToken}?token=${encodeURIComponent(token)}`;
    const response = await axios.get<ActionToken>(url);
    return response.data;
  }
);

// Fetch review by ID
export const fetchGbpReviewById = createAsyncThunk(
  'gbp/fetchReviewById',
  async ({ reviewId, accountId, token }: { reviewId: string; accountId: string, token: string }, { getState }) => {
    // const state = getState() as { gbpSlice: GbpState };
    // const originalToken = state.gbpSlice.originalToken;
    
    const url = `${BASE_URL}${GbpEndpoints.FetchReviewById.replace(':id', reviewId)}?accountId=${accountId}&token=${token}`;
    const response = await axios.get<GbpReview>(url, {
      headers: token ? { 'X-Action-Token': token } : {}
    });
    return response.data;
  }
);

// Reply to review
export const replyToGbpReview = createAsyncThunk(
  'gbp/replyToReview',
  async ({ accountId, reviewId, comment, token }: { accountId: string; reviewId: string; comment: string; token: string }, { getState }) => {
    const url = `${BASE_URL}${GbpEndpoints.ManageReviewReply.replace(':id', reviewId)}?accountId=${accountId}&token=${token}`;
    await axios.put(url, { comment }, {
      headers: token ? { 'X-Action-Token': token } : {}
    });
    return { reviewId, comment };
  }
);

// Generate review reply
export const generateGbpReviewReply = createAsyncThunk(
  'gbp/generateReply',
  async ({ accountId, reviewId, token }: { accountId: string; reviewId: string; token: string }, { getState }) => {
    const url = `${BASE_URL}${GbpEndpoints.GenerateReviewReply.replace(':id', reviewId)}?accountId=${accountId}&token=${token}`;
    const response = await axios.post<{ reviewId: string; suggestedReply: string }>(url, {}, {
      headers: token ? { 'X-Action-Token': token } : {}
    });
    return { reviewId: response.data.reviewId, generatedReply: response.data.suggestedReply };
  }
);

// Delete review reply
export const deleteGbpReviewReply = createAsyncThunk(
  'gbp/deleteReply',
  async ({ accountId, reviewId, token }: { accountId: string; reviewId: string; token: string }, { getState }) => {
    const url = `${BASE_URL}${GbpEndpoints.ManageReviewReply.replace(':id', reviewId)}?accountId=${accountId}&token=${token}`;
    await axios.delete(url, {
      headers: token ? { 'X-Action-Token': token } : {}
    });
    return reviewId;
  }
);

// Send video invitation
export const sendVideoInvitation = createAsyncThunk(
  'gbp/sendVideoInvitation',
  async ({ accountId, reviewId, phoneNumber, token }: { accountId: string; reviewId: string; phoneNumber: string; token: string }) => {
    const url = `${BASE_URL}${GbpEndpoints.SendInvitation.replace(':id', reviewId)}?accountId=${accountId}`;
    await axios.post(url, { phoneNumber }, {
      headers: {
        'X-Action-Token': token,
        'Content-Type': 'application/json'
      }
    });
    return { reviewId, phoneNumber };
  }
);

// Initialize and fetch review from token
export const initializeFromToken = createAsyncThunk(
  'gbp/initializeFromToken',
  async (token: string, { dispatch }) => {
    // First get the action token
    const actionTokenResult = await dispatch(getActionToken(token));
    
    if (getActionToken.fulfilled.match(actionTokenResult)) {
      const actionToken = actionTokenResult.payload;
      
      // Then fetch the review using the reviewId from context
      const reviewResult = await dispatch(fetchGbpReviewById({
        reviewId: actionToken.context.reviewId,
        accountId: actionToken.accountId,
        token: token
      }));
      
      if (fetchGbpReviewById.fulfilled.match(reviewResult)) {
        return {
          actionToken,
          review: reviewResult.payload,
          originalToken: token // Return the original token
        };
      } else {
        throw new Error('Failed to fetch review');
      }
    }
    
    throw new Error('Failed to get action token');
  }
);

const initialState: GbpState = {
  originalToken: null,
  actionToken: {
    data: null,
    loading: false,
    error: null,
  },
  reviews: {
    currentReview: null,
    generatingReply: {
      reviewId: null,
    },
    generatedReplies: {},
    submittingReviewId: null,
    loading: false,
    error: null,
  },
};

const gbpSlice = createSlice({
  name: 'gbp',
  initialState,
  reducers: {
    clearGeneratedReply: (state, action: PayloadAction<{ reviewId: string }>) => {
      delete state.reviews.generatedReplies[action.payload.reviewId];
    },
    clearError: (state) => {
      state.reviews.error = null;
      state.actionToken.error = null;
    },
    setCurrentReview: (state, action: PayloadAction<GbpReview>) => {
      state.reviews.currentReview = action.payload;
    },
    setOriginalToken: (state, action: PayloadAction<string>) => {
      state.originalToken = action.payload;
    },
    resetState: (state) => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get action token
      .addCase(getActionToken.pending, (state) => {
        state.actionToken.loading = true;
        state.actionToken.error = null;
      })
      .addCase(getActionToken.fulfilled, (state, action) => {
        state.actionToken.loading = false;
        state.actionToken.data = action.payload;
      })
      .addCase(getActionToken.rejected, (state, action) => {
        state.actionToken.loading = false;
        // state.actionToken.error = action.error.message || 'Failed to get action token';
      })
      
      // Initialize from token
      .addCase(initializeFromToken.pending, (state) => {
        state.actionToken.loading = true;
        state.reviews.loading = true;
        state.actionToken.error = null;
        state.reviews.error = null;
      })
      .addCase(initializeFromToken.fulfilled, (state, action) => {
        state.actionToken.loading = false;
        state.reviews.loading = false;
        state.actionToken.data = action.payload.actionToken;
        state.reviews.currentReview = action.payload.review;
        state.originalToken = action.payload.originalToken; // Store the original token
      })
      .addCase(initializeFromToken.rejected, (state, action) => {
        state.actionToken.loading = false;
        state.reviews.loading = false;
        // state.actionToken.error = action.error.message || 'Failed to initialize from token';
      })
      
      // Fetch review
      .addCase(fetchGbpReviewById.pending, (state) => {
        state.reviews.loading = true;
        state.reviews.error = null;
      })
      .addCase(fetchGbpReviewById.fulfilled, (state, action) => {
        state.reviews.loading = false;
        state.reviews.currentReview = action.payload;
      })
      .addCase(fetchGbpReviewById.rejected, (state, action) => {
        state.reviews.loading = false;
        // state.reviews.error = action.error.message || 'Failed to fetch review';
      })
      
      // Reply to review
      .addCase(replyToGbpReview.pending, (state, action) => {
        state.reviews.submittingReviewId = action.meta.arg.reviewId;
      })
      .addCase(replyToGbpReview.fulfilled, (state, action) => {
        state.reviews.submittingReviewId = null;
        if (state.reviews.currentReview && state.reviews.currentReview.reviewId === action.payload.reviewId) {
          state.reviews.currentReview.replyComment = action.payload.comment;
          state.reviews.currentReview.replyUpdateTime = new Date().toISOString();
        }
      })
      .addCase(replyToGbpReview.rejected, (state, action) => {
        state.reviews.submittingReviewId = null;
        // state.reviews.error = action.error.message || 'Failed to submit reply';
      })
      
      // Generate reply
      .addCase(generateGbpReviewReply.pending, (state, action) => {
        state.reviews.generatingReply.reviewId = action.meta.arg.reviewId;
      })
      .addCase(generateGbpReviewReply.fulfilled, (state, action) => {
        state.reviews.generatingReply.reviewId = null;
        state.reviews.generatedReplies[action.payload.reviewId] = action.payload.generatedReply;
      })
      .addCase(generateGbpReviewReply.rejected, (state, action) => {
        state.reviews.generatingReply.reviewId = null;
        // state.reviews.error = action.error.message || 'Failed to generate reply';
      })
      
      // Delete reply
      .addCase(deleteGbpReviewReply.fulfilled, (state, action) => {
        if (state.reviews.currentReview && state.reviews.currentReview.reviewId === action.payload) {
          state.reviews.currentReview.replyComment = null;
          state.reviews.currentReview.replyUpdateTime = null;
        }
      })
      .addCase(deleteGbpReviewReply.rejected, (state, action) => {
        // state.reviews.error = action.error.message || 'Failed to delete reply';
      });
  },
});

export const { clearGeneratedReply, clearError, setCurrentReview, setOriginalToken, resetState } = gbpSlice.actions;
export default gbpSlice.reducer; 